/**
 * 素材标签管理器
 * 负责素材标签的创建、查询、更新和删除操作。
 * @author Joey
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { jsonStore } from "@/manager/cache/localdb";
import { MaterialTagData, materialTagList } from "@/services/material-tag-list";
import { canUseWindow } from "@/utils/canuseWindow";
import { materialTagCreate } from "@/services/material-tag-create";
import { materialTagBatchDelete } from "@/services/material-tag-batch-delete";
import { materialTagUpdate } from "@/services/material-tag-update";
import { assetsMaterialManager } from "./assets-material-manager";
import { materialCopyBatchCopy, RequestParams } from "@/services/material-copy-batch-copy";
import { materialUploadBatchUpload, MaterialUploadBatchUploadRequest } from "@/services/material-upload-batch-upload";

interface CacheData {
  list: MaterialTagData[];
}

type TagsChangeEventDetail = { tags: MaterialTagData[] }
type TagsChangeEvent = CustomEvent<TagsChangeEventDetail>;

type AllFolderTagsChangeEventDetail = { allFolderTags: MaterialTagData[] }
type AllFolderTagsChangeEvent = CustomEvent<AllFolderTagsChangeEventDetail>;

// 缓存
class AssetsTagLocal {
  constructor(private storageKey: string) { }
  async get(folderId: number): Promise<CacheData | null> {
    return await jsonStore.get<CacheData>(`${this.storageKey}:${folderId}`);
  }
  async set(data: CacheData, folderId: number): Promise<void> {
    await jsonStore.set(`${this.storageKey}:${folderId}`, data);
  }
  async clear(folderId: number): Promise<void> {
    await jsonStore.set(`${this.storageKey}:${folderId}`, null);
  }
}

// 远程接口
class AssetsTagRemote {
  constructor(private cache: AssetsTagLocal) { }
  async getCached(folderId: number) {
    return await this.cache.get(folderId);
  }
  // 标签列表
  async fetchTags(folderId?: number) {
    const data = await materialTagList(folderId);
    await this.cache.set({ list: data.list }, folderId);
    return data.list;
  }
  // 创建标签
  async createTag(folderId: number, tagName: string) {
    return await materialTagCreate({ folderId, tagName });
  }
  // 批量删除标签
  async deleteTags(tagIds: number[]) {
    return await materialTagBatchDelete(tagIds);
  }
  // 更新标签
  async updateTag({ id, folderId, tagName }: { id: number, folderId: number, tagName: string }) {
    return await materialTagUpdate({ id, folderId, tagName });
  }
  // 批量复制/移动标签
  async batchCopyMove({ itemIds, target, deleteOriginalItem }: RequestParams) {
    return await materialCopyBatchCopy({ itemIds, target, deleteOriginalItem });
  }
  // 批量上传标签
  async batchUpload(data: MaterialUploadBatchUploadRequest[]) {
    return await materialUploadBatchUpload(data);
  }
}

// 素材标签管理器
export class AssetsTagManager {
  private static instance: AssetsTagManager;
  private readonly STORAGE_KEY = "MIUTA_ASSETS_TAG";
  private cache: AssetsTagLocal;
  private remote: AssetsTagRemote;

  private tags: MaterialTagData[] = [];
  private allFolderTags: MaterialTagData[] = [];
  private folderId: number = null;

  private constructor() {
    this.cache = new AssetsTagLocal(this.STORAGE_KEY);
    this.remote = new AssetsTagRemote(this.cache);
  }

  public static getInstance(): AssetsTagManager {
    if (!AssetsTagManager.instance) {
      AssetsTagManager.instance = new AssetsTagManager();
    }
    return AssetsTagManager.instance;
  }

  // 初始化
  public async init(folderId: number) {
    try {
      this.setFolderId(folderId);
      // 获取缓存指定文件夹的标签列表
      const cached = await this.remote.getCached(folderId);
      if (cached && cached.list && cached.list.length) {
        this.setTags(folderId, cached.list);
        this.fetchTags(folderId);
        return;
      }
    } catch (error) {
      console.error("Failed to initialize material tags:", error);
      this.setTags(Number(folderId), []);
      throw error;
    }
    // 缓存为空时，从远程接口获取标签列表
    await this.fetchTags(folderId);
  }

  // 获取指定素材文件夹标签列表
  async fetchTags(folderId?: number) {
    try {
      const tags = await this.remote.fetchTags(folderId);
      this.setTags(folderId, tags);
      return tags;
    } catch (error) {
      console.error("Failed to fetch material tags:", error);
      throw error;
    }
  }

  // 获取所有素材文件夹标签列表
  async fetchAllFolderTags() {
    try {
      const allFolderTags = await this.remote.fetchTags();
      this.setAllFolderTags(allFolderTags);
      return allFolderTags;
    } catch (error) {
      console.error("Failed to fetch all material folder tags:", error);
      throw error;
    }
  }

  // 创建标签
  async createTag(tagName: string, folderId?: number) {
    try {
      await this.remote.createTag(folderId || this.getFolderId(), tagName);
    } catch (error) {
      console.error("Failed to create material tag:", error);
      throw error;
    }
  }

  // 批量删除标签
  async deleteTags(tagIds: number[]) {
    try {
      await this.remote.deleteTags(tagIds);
      // 更新标签列表
      const newTags = this.getTags().filter(tag => !tagIds.includes(tag.id));
      this.setTags(this.getFolderId(), newTags);
    } catch (error) {
      console.error("Failed to delete material tags:", error);
      throw error;
    }
  }

  // 更新标签
  async updateTag({ id, tagName }: { id: number, tagName: string }) {
    try {
      // 更新标签列表
      const newTags = this.getTags().map(tag => tag.id === id ? { ...tag, tagName } : tag);
      this.setTags(this.getFolderId(), newTags);

      await this.remote.updateTag({
        id,
        folderId: this.getFolderId(),
        tagName,
      });
    } catch (error) {
      console.error("Failed to update material tag:", error);
      throw error;
    }
  }

  // 批量复制/移动标签
  async batchCopyMove({ itemIds, target, deleteOriginalItem }: RequestParams) {
    try {
      await this.remote.batchCopyMove({ itemIds, target, deleteOriginalItem });
    } catch (error) {
      console.error("Failed to batch copy/move material tags:", error);
      throw error;
    }
  }

  // 批量上传
  async batchUpload(data: MaterialUploadBatchUploadRequest[]) {
    try {
      await this.remote.batchUpload(data);
    } catch (error) {
      console.error("Failed to batch upload material tags:", error);
      throw error;
    }
  }

  // ==================== 读取写入操作 ====================
  public getTags() {
    return this.tags;
  }
  public setTags(id: number, tags: MaterialTagData[]) {
    this.tags = tags;
    this.cache.set({ list: tags }, id);
    this.dispatchTagsChange();
  }

  public getAllFolderTags() {
    return this.allFolderTags;
  }
  public setAllFolderTags(allFolderTags: MaterialTagData[]) {
    this.allFolderTags = allFolderTags;
    this.dispatchAllFolderTagsChange();
  }

  public getFolderId() {
    return this.folderId;
  }
  public setFolderId(folderId: number) {
    this.folderId = folderId;
  }

  // ==================== 订阅通知操作 ====================
  // 标签变化通知
  private dispatchTagsChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<TagsChangeEventDetail>(`${this.STORAGE_KEY}:tags-change`, {
      detail: { tags: this.tags }
    });
    window.dispatchEvent(event);
  }
  public onTagsChange(listener: (e: TagsChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:tags-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }
  // 所有素材文件夹标签变化通知
  private dispatchAllFolderTagsChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<AllFolderTagsChangeEventDetail>(`${this.STORAGE_KEY}:all-folder-tags-change`, {
      detail: { allFolderTags: this.allFolderTags }
    });
    window.dispatchEvent(event);
  }
  public onAllFolderTagsChange(listener: (e: AllFolderTagsChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:all-folder-tags-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }
}

// =============================== Hooks ===============================
// 标签数据
const useAssetsTagData = () => {
  const toast = useToast();
  const params = useParams<{ id: string }>();

  const [tags, setTags] = useState(assetsTagsManager.getTags());
  const [loading, setLoading] = useState(false);

  const init = async () => {
    setLoading(true);
    try {
      await assetsTagsManager.init(Number(params.id));
    } catch (error) {
      console.error("Failed to initialize material tags:", error);
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!params.id) return;
    init();
  }, [params.id]);

  useEffect(() => {
    return assetsTagsManager.onTagsChange((e) => {
      setTags(e.detail.tags);
    });
  }, []);

  return {
    tags,
    loading,
  }
}

// 创建标签
const useCreateAssetsTag = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const createTag = async (tagName: string, folderId?: number) => {
    setLoading(true);
    try {
      if (assetsTagsManager.getTags().length >= 200) {
        toast.toast({
          description: "You can create up to 200 tags.",
        });
        return;
      }
      await assetsTagsManager.createTag(tagName, folderId);
      // 刷新标签列表
      if (!folderId || folderId === assetsTagsManager.getFolderId()) {
        await assetsTagsManager.fetchTags(assetsTagsManager.getFolderId());
      }
      toast.toast({
        description: t("assets.material_item.tags.create_success"),
      });
    } catch (error) {
      console.error("Failed to create material tag:", error);
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    createTag,
    loading,
  }
}

// 批量删除标签
const useDeleteAssetsTags = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const deleteTags = async (tagIds: number[]) => {
    setLoading(true);
    try {
      await assetsTagsManager.deleteTags(tagIds);
      toast.toast({
        description: t("assets.material_item.tags.delete_success"),
      });
    } catch (error) {
      console.error("Failed to delete material tags:", error);
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  return {
    deleteTags,
    loading,
  }
}

// 更新标签
const useRenameAssetsTag = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const renameTag = async ({ id, name }: { id: number, name: string }) => {
    setLoading(true);
    try {
      await assetsTagsManager.updateTag({ id, tagName: name });
    } catch (error) {
      console.error("Failed to rename material tag:", error);
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  return {
    renameTag,
    loading,
  }
}

// 所有素材文件夹标签数据
const useFolderGroupTagData = () => {
  const toast = useToast();
  const params = useParams<{ id: string }>();

  const [tags, setTags] = useState(assetsTagsManager.getAllFolderTags());
  const [folders, setFolders] = useState(assetsMaterialManager.getMaterialFolders());
  const [loading, setLoading] = useState(false);
  const initRef = useRef(false);

  const fetchAllTags = async () => {
    if (!initRef.current) {
      setLoading(true);
    }
    try {
      await assetsTagsManager.fetchAllFolderTags();
      initRef.current = true;
    } catch (error) {
      console.error("Failed to fetch all material folder tags:", error);
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  // 素材文件夹标签分组
  const folderGroupTags = useMemo(() => {
    const group = folders.map(item => {
      return {
        ...item,
        tags: tags.filter(tag => tag.folderId === item.id),
      }
    })
    return group;
  }, [folders, tags]);

  useEffect(() => {
    fetchAllTags();
  }, [params.id]);

  useEffect(() => {
    return assetsTagsManager.onAllFolderTagsChange((e) => {
      setTags(e.detail.allFolderTags);
    });
  }, []);

  useEffect(() => {
    return assetsMaterialManager.onMaterialFoldersChange((e) => {
      setFolders(e.detail.materialFolders);
    });
  }, []);

  return {
    tags,
    loading,
    folderGroupTags,
    fetchAllTags,
  }
}

// 批量复制/移动标签
const useBatchCopyMoveAssetsTags = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const batchCopyMove = async ({ itemIds, target, deleteOriginalItem }: RequestParams, refreshTags: boolean = true) => {
    setLoading(true);
    try {
      await assetsTagsManager.batchCopyMove({ itemIds, target, deleteOriginalItem });
      // 更新标签列表
      if (refreshTags) {
        await assetsTagsManager.fetchTags(assetsTagsManager.getFolderId());
      }
    } catch (error) {
      console.error("Failed to batch copy/move material tags:", error);
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  return {
    batchCopyMove,
    loading,
  }
}

// 将链接批量上传到文件夹和标签
const useBatchUploadFolderTags = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const batchUpload = async (data: MaterialUploadBatchUploadRequest[]) => {
    setLoading(true);
    try {
      await assetsTagsManager.batchUpload(data);
      toast.toast({ description: t("ai_creation.save_to_material_success") });
    } catch (error) {
      toast.showToastByCode(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    batchUpload,
    loading,
  }
}

export const assetsTagsHook = {
  useAssetsTagData,
  useCreateAssetsTag,
  useDeleteAssetsTags,
  useRenameAssetsTag,
  useFolderGroupTagData,
  useBatchCopyMoveAssetsTags,
  useBatchUploadFolderTags,
}
// 导出单例实例
export const assetsTagsManager = AssetsTagManager.getInstance();